import Util from '../../../Common/Util';
/**
 * 정정 요청 페이지 상태(S) + 부트 매핑 + 보조 헬퍼 — Defense.js _spaInit 에서 분리 (Phase2-B).
 *   makeStore(boot) → { S, getDeadlineRemaining, saveLastScreen, loadLastScreen, stateToScreen, applyLoad }
 *
 * S 는 화면 모듈(components/screens) + bind 이벤트가 공유하는 단일 상태 객체.
 * 소명 트랙은 2026-10 에 폐지됐다 — 링크로 들어온 업체는 정정(본인 아님) 요청만 한다.
 */
export function makeStore(boot) {
    const LS_LAST_SCR = 'akdeok_somyung_last_scr_v1';

    function getDemoDeadline() {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        d.setHours(10, 33, 0, 0);
        return `${d.getFullYear()}-${Util.pad(d.getMonth() + 1, 2)}-${Util.pad(d.getDate(), 2)} ${Util.pad(d.getHours(), 2)}:${Util.pad(d.getMinutes(), 2)}`;
    }

    // 상태 — boot 페이로드 매핑. report 본문은 컨트롤러 boot, 증빙사진은 {id,url} 객체 배열.
    const r0 = boot.report || {};
    const S = {
        scr: 'auth_first',
        // 업체 본인 번호 — 서버 boot 은 전화를 싣지 않으므로(마스킹 명세 §2-4) 보통 비어 있다가,
        // 본인인증을 통과하면 그 번호로 채워진다(AuthSheet / applyLoad 의 res.auth_phone).
        // ⚠ 모르는 값을 '010-0000-0000' 같은 가짜로 채우지 않는다 — 업체가 자기 번호인 줄 안다(2026-10-07 상필 님 QA).
        reportPhone: r0.ph || '',
        userName: '',
        // SMS 인증 (PIN 대체)
        authPhone: '',
        authCode: '',
        authSent: false,
        authVerified: !!boot.verified,
        authVerifiedAt: +(boot.verifiedAt || 0),
        smsOk: false,       // 문자 본인인증 실제 통과(서버 SESS_SMS_OK) — load 응답 sms_ok / 인증 성공 시 true. 통화·정정 진입 때 인증 시트 생략 기준

        agreedPrivacy: false,
        skipAgree: false,   // (반려 재작성은 폐지 — 동의 숨김 분기는 NotmeForm 호환용으로만 남김)
        // 정정(본인 아님) 요청
        notmePhotos: [],
        notmeReason: '',
        // 인증 후 이동할 곳 — 'notme'(정정 요청서) | 'call'(제보자와 통화 시트)
        afterAuth: 'notme',
        report: {
            co:           r0.co           || (boot.demo ? '바람스카이연합' : '체불업체'),
            brn:          r0.brn          || (boot.demo ? '123-45-67890' : ''),
            rep:          r0.rep          || (boot.demo ? '김대표' : '제보자'),
            ph:           r0.ph           || (boot.demo ? '010-1234-4787' : ''),
            extraPhones:  r0.extraPhones  || (boot.demo ? ['010-1784-9921'] : []),
            workDate:     r0.workDate     || (boot.demo ? '25-09-12 ~ 25-09-15' : ''),
            equipment:    r0.equipment    || (boot.demo ? '스카이 3.5T' : ''),
            location:     r0.location     || (boot.demo ? '서울 관악구' : ''),
            amount:       r0.amount       || (boot.demo ? '4,000,000원' : ''),
            content:      r0.content      || (boot.demo ? '기성이 들어오지 않는다며 계속해서 장비임대료 1년 넘어서도 처리해주지 않음.' : ''),
            registeredAt: r0.registeredAt || (boot.demo ? '25-11-19 10:33' : ''),
            deadline:     r0.deadline     || (boot.demo ? getDemoDeadline() : ''),
            rejectedAt:        r0.rejectedAt        || (boot.demo ? '25-11-21 09:15' : ''),
            notmeRejectReason: r0.notmeRejectReason || (boot.demo ? '제출하신 증빙 서류로는 본인 아님을 확인하기 어렵습니다. 통신사에서 발급한 최신 서류(발급일 1개월 이내)를 다시 제출해 주세요.' : ''),
        },
    };

    /* 마감까지 남은 시간.
     * ⚠️ 운영: 서버 시각 동기화 — 현재는 클라이언트 Date 기준이라 기기 시계 틀리면 표시도 어긋남. */
    function getDeadlineRemaining() {
        const m = (S.report.deadline || '').match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
        if (!m) return { expired: true };
        const target = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
        const diff = target - new Date();
        if (diff <= 0) return { expired: true };
        return {
            expired: false,
            days:  Math.floor(diff / 86400000),
            hours: Math.floor((diff % 86400000) / 3600000),
            mins:  Math.floor((diff % 3600000) / 60000),
        };
    }

    function saveLastScreen() { $j.Common.Store.local.set(LS_LAST_SCR, S.scr); }         // Store 가 try/catch 내장
    function loadLastScreen() { return $j.Common.Store.local.get(LS_LAST_SCR); }

    // 정정 상태(defense_status) → 진입 화면. 신고가 철회(RECALLED)된 건은 Defense.js 가 boot.reportStatus 로 먼저 가른다.
    function stateToScreen(state) {
        switch (String(state || '').toUpperCase()) {
            case 'SUBMITTED': return 'notme_done';
            case 'APPROVED':  return 'notme_approved';
            case 'REJECTED':  return 'notme_rejected';
            case 'EXPIRED':   return 'registered';
            case 'RECALLED':  return 'recalled';
            case 'DRAFT':     return 'notme';
            case 'NOTIFIED':
            default:          return 'auth_first';
        }
    }

    // load 응답(/b/defense/ajax/load) → 상태 S 채우기 (재접속 시 진행/결과 화면 복원).
    function applyLoad(res) {
        const rep = res.report || {};
        const def = res.defense || {};
        const photos = (res.defense_photos || []).map(p => ({ id: p.id, url: p.url }));

        // 체불 등록 내역 — 모든 값은 서버에서 이미 마스킹됨(명세 2-4). 클라 추가 마스킹 금지.
        //   노출 = 업체명·작업장주소·작업일자·사용장비·제보자명 뿐. 전화/금액/대표자/사업자번호/제보상세/제보자 증빙사진은 서버가 미전송(인증 뒤에도).
        if (rep.co_name)       S.report.co = rep.co_name;
        if (rep.reporter_name) S.report.rep = rep.reporter_name;
        if (rep.work_day)      S.report.workDate = rep.work_day;
        if (rep.equipment)     S.report.equipment = rep.equipment;
        if (rep.work_place)    S.report.location = rep.work_place;
        if (rep.created_at)    S.report.registeredAt = rep.created_at;
        if (res.deadline)  S.report.deadline = res.deadline;
        S.smsOk = !!res.sms_ok;
        // 인증을 통과한 번호(서버 세션). 새로고침·재진입으로 S.authPhone 이 비어도 정정 요청서의 연락처 칸이 채워진다.
        if (res.auth_phone) S.reportPhone = Util.phoneNumber(res.auth_phone);
        if (def.reject_reason) S.report.notmeRejectReason = def.reject_reason;

        // 제출 본문 — 결과 화면 렌더용
        if (def.name)    S.userName = def.name;
        S.notmeReason = def.content || '';
        S.notmePhotos = photos;
    }

    return { S, getDeadlineRemaining, saveLastScreen, loadLastScreen, stateToScreen, applyLoad };
}
